import { AfterContentInit, Component, ElementRef, Inject, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { FormArray, FormControl, FormGroup, ValidatorFn, Validators } from "@angular/forms";
import { ErrorStateMatcher } from "@angular/material/core";
import {
	MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
	MatLegacyDialog as MatDialog,
	MatLegacyDialogRef as MatDialogRef,
} from "@angular/material/legacy-dialog";
import { MatLegacyMenuTrigger as MatMenuTrigger } from "@angular/material/legacy-menu";
import { ElRefDirective } from "@app/_directives/el-ref/el-ref.directive";
import { clampDay } from "@app/_helpers/date-fns";
import { ObserveFormGroupErrors, useFormErrorObservable } from "@app/_helpers/get-error-observable";
import { pushError } from "@app/_helpers/globalErrorHandler";
import parseSubscriptionAsStatus from "@app/_helpers/parseSubscriptionAsStatus";
import { hasUserRoleManager, isAdmin, isProjectManager, isProjectUser } from "@app/_helpers/permission";
import { toPromise } from "@app/_helpers/promise";
import {
	getTimesFutureLockDate,
	getTimesLockedDate,
	isTimeLockedForDate,
	isTimeLockedForDateFuture,
} from "@app/_helpers/timeLock";
import userProjectFind from "@app/_helpers/userProjectFind";
import {
	canBookOnTask,
	coerceTimeFormat,
	createRxValue,
	DEFAULT_PERMISSION_GROUPS,
	distinctUntilChangedJson,
	fromRxValue,
	hasPermission,
	splitLongRangeToDay,
	toBookingUserSettings,
} from "@app/_helpers/utils";
import { ClockService } from "@app/_services/clock.service";
import { CustomValidators } from "@app/_validators/custom-validators";
import { MaterialScaleVariable } from "@app/animations/fade";
import { AppService } from "@app/app.service";
import {
	parseDurationAsFormat,
	parseDurationSeconds,
} from "@app/components/duration-input-control/duration-input-utils";
import {
	UserSinglePickerDialogComponent,
	UserSinglePickerDialogData,
} from "@app/components/user-single-picker-dialog/user-single-picker-dialog.component";
import { DeletionUndoService } from "@app/services/deletion/deletion-undo.service";
import { EditUndoService } from "@app/services/edit-undo/edit-undo.service";
import { MixedTimesSyncService } from "@app/services/mixed-times-sync.service";
import {
	ClientProjectPickerStepperData,
	ClientProjectPickerStepperDialogComponent,
} from "@app/shared/dialogs/client-project-picker-stepper-dialog/client-project-picker-stepper-dialog.component";
import { environment } from "@env/environment";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { TranslateService } from "@ngx-translate/core";
import {
	FeedEntry,
	FeedQuery,
	Logger,
	MyTimesQuery,
	MyTimesService,
	Project,
	ProjectsQuery,
	Tag,
	TagType,
	Task,
	Time,
	TimesService,
	UserSettings,
	UserSettingsQuery,
	Workspace,
} from "@timeghost/web-api";
import { MyTimesStore } from "@timeghost/web-api/lib/stores/myTimes/myTimes.store";
import { CalendarEventTimesChangedEvent } from "angular-calendar";
import { CalendarEvent } from "calendar-utils";
import {
	addMinutes,
	addSeconds,
	differenceInSeconds,
	endOfDay,
	format as formatString,
	intervalToDuration,
	isValid,
	parse as parseFromString,
	roundToNearestMinutes,
	setSeconds,
	startOfDay,
} from "date-fns/esm";
import { addHours, isSameDay, parse as parsefp, parseISO, subHours, subMinutes } from "date-fns/esm/fp";
import produce from "immer";
import { flow } from "lodash";
import { MediaObserver } from "ngx-flexible-layout";
import { BehaviorSubject, combineLatest, firstValueFrom } from "rxjs";
import { distinctUntilChanged, filter, map, pairwise, shareReplay, startWith, tap } from "rxjs/operators";
import { TagDialogData, TagPickerDialogComponent } from "../dialogs/tag-picker-dialog/tag-picker-dialog.component";
import { RecordToolbarService } from "../record-toolbar/record-toolbar.service";
import { TimeDatePickerComponent } from "../time-date-picker/time-date-picker.component";
import { TimeDatePickerConfig } from "../time-date-picker/time-date-picker-config";

export const verifyBlockTimePastValidate: (
	currentuser: () => UserSettings,
	project: () => Project,
	start: () => string,
	end: () => string
) => ValidatorFn = (_currentuser, _project, _start, _end) => {
	return (form: FormControl<Date>) => {
		const blockTimes = _currentuser().workspace.settings?.blockTimes;
		if (!blockTimes) return null;
		const date = form.getRawValue();
		const currentuser = _currentuser();
		const isAdmin = !!currentuser.workspace.users.find((x) => x.admin && x.id === currentuser.id);
		if (isAdmin) return null;

		// add check for ProjectManager
		const project: Project = _project();
		let isManager = false;
		if (project) {
			isManager = userProjectFind(_currentuser(), project, { projectManager: true, workspaceAdmin: true });
			if (blockTimes.pastMode == "timePeriod" && blockTimes.range == "hours") {
				const timeString = _start();
				const [hours, minutes] = timeString.split(":").map(Number);
				const start = date;
				if (start) start.setHours(hours, minutes, 0, 0);
				if (isTimeLockedForDate(_currentuser(), project, start)) {
					return {
						blockEdit: true,
					};
				}
			}
			if (blockTimes.futureMode == "timePeriod" && blockTimes.futureRange == "hours") {
				const timeString = _end();
				const [hours, minutes] = timeString.split(":").map(Number);
				const end = date;
				if (end) end.setHours(hours, minutes, 0, 0);
				if (isTimeLockedForDateFuture(_currentuser(), project, end)) {
					return {
						blockEditFuture: true,
					};
				}
			}
			if (
				isTimeLockedForDate(_currentuser(), project, date) ||
				isTimeLockedForDateFuture(_currentuser(), project, date)
			) {
				return {
					blockEdit: true,
				};
			}
		}
	};
};

const log = new Logger("TimeTrackerCalendarUpdateDialogComponent");
class CustomFieldErrorStateMatcher implements ErrorStateMatcher {
	constructor(private ctrl: FormControl) {}
	isErrorState: ErrorStateMatcher["isErrorState"] = (control, form) => {
		return this.ctrl && this.ctrl.invalid && (this.ctrl.dirty || this.ctrl.touched);
	};
}
interface SplitTime {
	end: Date;
	format: string;
	start: Date;
}
type SplitDayControl = FormGroup<{
	start: FormControl<string>;
	end: FormControl<string>;
}>;
@UntilDestroy()
@ObserveFormGroupErrors()
@Component({
	selector: "app-time-tracker-calendar-update-dialog",
	templateUrl: "./time-tracker-calendar-update-dialog.component.html",
	styleUrls: ["./time-tracker-calendar-update-dialog.component.scss"],
	animations: [MaterialScaleVariable(0.875)],
})
export class TimeTrackerCalendarUpdateDialogComponent implements OnInit, AfterContentInit {
	private _isLoading = new BehaviorSubject<boolean>(false);
	readonly isLoading$ = this._isLoading.asObservable().pipe(distinctUntilChanged());
	get isLoading() {
		return this._isLoading.getValue();
	}
	set isLoading(val: boolean) {
		this._isLoading.next(val);
	}
	readonly refTimeEntry = createRxValue(null);
	group = new FormGroup(
		{
			name: new FormControl("", [
				CustomValidators.minLength(3),
				CustomValidators.entityValidator(
					() => this.userSettingsQuery.getValue(),
					(user, ctrl) => {
						if (user.workspace?.settings?.requireName && !ctrl.value) {
							return {
								required: true,
							};
						}
						return null;
					}
				),
			]),
			project: new FormControl<any>(null, [
				(ctrl) => {
					const user = this.userSettingsQuery.getValue();
					if (user.workspace?.settings?.requireProject) {
						const projectValue = ctrl.value as Project;
						if (!projectValue || projectValue.useAsDefault)
							return {
								required: true,
							};
					}
					return null;
				},
			]),
			task: new FormControl(null, [
				(ctrl) => {
					const user = this.userSettingsQuery.getValue();
					if (user.workspace?.settings?.requireTask) {
						if (!ctrl.value?.id)
							return {
								required: true,
							};
					}
					return null;
				},
			]),
			billable: new FormControl(false),
			date: new FormControl(null, {
				validators: [
					verifyBlockTimePastValidate(
						() => this.userSettingsQuery.getValue(),
						() => {
							const pId = this.group?.getRawValue()?.project;
							let p: Project = null;
							if (pId) p = this.projectsQuery.getAll().find((x) => x.id == pId.id);
							return p;
						},
						() => {
							if (!this.group) return "00:00";
							const start: string = this.group.controls.start.getRawValue();
							if (!start) return null;
							return start;
						},
						() => {
							if (!this.group) return "23:59";
							const end: string = this.group.controls.end.getRawValue();
							if (!end) return null;
							return end;
						}
					),
					Validators.required,
				],
			}),
			start: new FormControl(null),
			end: new FormControl(null, (ctrl) => {
				if (!this.isRecording) return Validators.required(ctrl);
				return null;
			}),
			duration: new FormControl(null),
			tags: new FormControl<Tag[]>([]),
			user: new FormControl<Workspace["users"][0]>(null),
		},
		{ updateOn: "change", validators: [] }
	);
	defaultProject: Project;
	events$ = combineLatest([
		this.group.valueChanges.pipe(distinctUntilChangedJson()),
		this.myTimesQuery.selectAll().pipe(startWith(this.myTimesQuery.getAll())),
		this.feedQuery.selectAll(),
	]).pipe(
		map(
			([groupValue, times, feed]: [
				{ date: Date; name: string; start: string; end: string; duration: number },
				Time[],
				FeedEntry[],
			]) => {
				const date: Date = groupValue?.date || new Date();
				const duration = !this.stateVisibleMode && parseDurationSeconds(groupValue.duration);
				const activeTime: CalendarEvent = {
					id: this.data.meta.time.outlookCalenderReference || "custom",
					title: groupValue.name,
					...(this.stateVisibleMode
						? {
								start: flow((val) => parseFromString(val, "HH:mm", date))(groupValue.start),
								end: this.isRecording
									? new Date()
									: flow((val) => parseFromString(val, "HH:mm", date))(groupValue.end),
							}
						: {
								// duration
								start: flow(startOfDay)(date),
								end: flow(startOfDay, (val) => addSeconds(val, duration))(date),
							}),
					draggable: !this.isRecording,
					resizable: {
						beforeStart: !this.isRecording,
						afterEnd: !this.isRecording,
					},
					cssClass: "event-item event-item-active",
					color: {
						primary: "#51BF43",
						secondary: "#51BF43",
					},
				};
				log.debug("current", activeTime);
				return times
					.filter(
						(x) =>
							x.start && x.end && (isSameDay(parseISO(x.start))(date) || isSameDay(parseISO(x.end))(date))
					)
					.map(
						(t) =>
							<CalendarEvent>{
								id: t.outlookCalenderReference || t.id,
								title: t.name,
								start: new Date(t.start),
								end: new Date(t.end),
								allDay: false,
								draggable: false,
								resizable: {
									beforeStart: false, // this allows you to configure the sides the event is resizable from
									afterEnd: false,
								},
								cssClass: "event-item",
							}
					)
					.concat(
						feed
							.filter((t) => t.calendar)
							.map(
								(t) =>
									<CalendarEvent>{
										id: "feed_" + t.id,
										title: t.name,
										start: new Date(t.start),
										end: new Date(t.end),
										allDay: false,
										draggable: false,
										resizable: {
											beforeStart: false, // this allows you to configure the sides the event is resizable from
											afterEnd: false,
										},
										cssClass: "event-item",
									}
							)
					)
					.concat(activeTime);
			}
		),
		shareReplay(),
		tap((x) => log.debug(x))
	);
	currentFormat$ = this.appService.timeFormat$;
	readonly now$ = this.clock.now$.pipe(map((now) => roundToNearestMinutes(now, { nearestTo: 1 })));
	constructor(
		private ref: MatDialogRef<TimeTrackerCalendarUpdateDialogComponent>,
		@Inject(MAT_DIALOG_DATA)
		private data: CalendarEvent<{
			time: Time;
			oldStart?: Date;
			oldEnd?: Date;
			forceUpdate?: boolean;
			fetchFromServer?: boolean;
		}>,
		private myTimes: MyTimesService,
		private myTimesQuery: MyTimesQuery,
		private timesService: TimesService,
		private projectsQuery: ProjectsQuery,
		private media: MediaObserver,
		private userSettingsQuery: UserSettingsQuery,
		private recordService: RecordToolbarService,
		private dialog: MatDialog,
		private translate: TranslateService,
		private feedQuery: FeedQuery,
		private appService: AppService,
		private el: ElementRef<HTMLElement>,
		private deletionService: DeletionUndoService,
		private editUndoService: EditUndoService,
		private mixedTimesSyncService: MixedTimesSyncService,
		private clock: ClockService
	) {
		this.defaultProject = this.projectsQuery.getAll().find((x) => x.useAsDefault === true);
	}
	ngAfterContentInit(): void {
		this.group.controls["project"].markAsDirty(), this.group.controls["task"].markAsDirty();
		setTimeout(() => {
			this.focusCalendarNow();

			this.group.updateValueAndValidity();
		}, 50);
	}
	get isRecording() {
		const time = this.data.meta.time;
		return time && !time?.end;
	}
	readonly recordingDuration$ = combineLatest([
		this.group.valueChanges.pipe(
			map((v) => ({ start: v?.start as string, date: v?.date as Date })),
			startWith({ start: this.group.value?.start, date: this.group.value?.date }),
			distinctUntilChanged((a, b) => a.start === b.start && a.date?.getTime() === b.date?.getTime())
		),
		this.now$,
	]).pipe(
		map(([{ start, date }, now]) => {
			if (!this.isRecording || !start || !date) return null;
			const startDate = parseFromString(start, "HH:mm", date);
			if (!isValid(startDate)) return null;
			const sec = Math.max(0, differenceInSeconds(now, startDate));
			const h = Math.floor(sec / 3600);
			const m = Math.floor((sec % 3600) / 60);
			return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
		})
	);
	get isOwnTime(): boolean {
		const timeUserId = this.data.meta.time?.user?.id;
		if (!timeUserId) return true;
		return timeUserId === this.userSettingsQuery.getValue().id;
	}
	get canStopRecording(): boolean {
		const time = this.data?.meta?.time;
		return this.isRecording && (this.isOwnTime || !!time?.currentUserCanEdit);
	}
	get canDelete(): boolean {
		const time = this.data?.meta?.time;
		return !!time?.id && !!time.currentUserCanEdit;
	}
	getProjectColor(id: string) {
		return this.projectsQuery.getEntity(id)?.color;
	}
	get requireSplit() {
		const start = this.data.start,
			now = new Date();
		return this.isRecording && start.getDate() !== now.getDate();
	}
	readonly splitTimes = createRxValue<SplitTime[]>([]);
	readonly splitTimes$ = this.splitTimes.asObservable();
	readonly splitForm = new FormArray<SplitDayControl>([]);
	readonly splitTimes$valid = this.splitTimes$.pipe(
		map((times) => {
			if (!this.requireSplit) return true;
			return !!times?.length;
		})
	);
	private recordingSplitSegment: SplitTime = null;
	/** the segment of the still running day, until the user removes that row */
	get hasRecordingSplitRow(): boolean {
		return !!this.recordingSplitSegment && (this.splitTimes.value ?? []).includes(this.recordingSplitSegment);
	}
	readonly splitStopsRecording$ = this.splitTimes$.pipe(map(() => this.requireSplit && !this.hasRecordingSplitRow));
	get splitDateFormat() {
		const lang = this.userSettingsQuery.getValue()?.settings?.languageSetting;
		const isDE = lang?.split("-", 2)?.[0]?.toLowerCase() === "de";
		return isDE ? "dd.MM" : "MM/dd";
	}
	get hasSingleSplitRow() {
		return (this.splitTimes.value ?? []).length === 1;
	}
	getSplitGroup(index: number): SplitDayControl {
		return this.splitForm.at(index) as SplitDayControl;
	}
	removeSplitDay(index: number) {
		if (index <= 0) return;
		this.splitTimes.update((items) => {
			items.splice(index, 1);
			return items;
		}, true);
		this.splitForm.removeAt(index);
	}
	private rebuildSplitForm(segments: SplitTime[]) {
		this.splitForm.clear();
		for (const seg of segments) {
			this.splitForm.push(
				new FormGroup({
					start: new FormControl(formatString(seg.start, "HH:mm"), {
						nonNullable: true,
						validators: [Validators.required, CustomValidators.validDateFormat("HH:mm")],
					}),
					end: new FormControl(formatString(seg.end, "HH:mm"), {
						nonNullable: true,
						validators: [Validators.required, CustomValidators.validDateFormat("HH:mm")],
					}),
				})
			);
		}
		const first = this.splitForm.at(0) as SplitDayControl;
		if (!first) return;
		// the first row holds the start of the running time, keep it validated by the main group
		first.controls.start.valueChanges.pipe(distinctUntilChanged(), untilDestroyed(this)).subscribe((start) => {
			if (first.controls.start.invalid) return;
			this.group.controls.start.patchValue(start);
		});
	}
	private getSplitSegmentsFromForm(): SplitTime[] {
		const bases = this.splitTimes.value ?? [];
		return this.splitForm.controls.map((ctrl, i) => {
			const base = bases[i];
			const day = startOfDay(base?.start ?? new Date());
			const { start, end } = ctrl.getRawValue();
			return {
				start: parseFromString(start, "HH:mm", day),
				end: parseFromString(end, "HH:mm", day),
				format: base?.format ?? this.splitDateFormat,
			};
		});
	}
	readonly workspace$isAdmin = fromRxValue(
		this.userSettingsQuery.select((x) => hasPermission(DEFAULT_PERMISSION_GROUPS.Admin, x))
	);
	readonly canChangeUser = fromRxValue(
		combineLatest([
			this.userSettingsQuery.select(),
			this.group.valueChanges.pipe(
				map((x) => x?.project),
				startWith(this.group.value?.project),
				distinctUntilChanged((a, b) => a?.id === b?.id)
			),
		]).pipe(
			map(
				([user, project]) =>
					isAdmin(user) ||
					hasUserRoleManager(user) ||
					(!!project && isProjectManager(user.id, user.workspace, project))
			)
		)
	);
	readonly postDiffUser$ = combineLatest([
		this.group.valueChanges.pipe(
			map((x) => x?.user?.id),
			distinctUntilChanged()
		),
		this.userSettingsQuery.select(),
	]).pipe(map(([uid, user]) => uid && uid !== user.id));
	get forceUpdate() {
		return this.data?.meta?.forceUpdate;
	}
	get isMobile() {
		return this.media.isActive(["xs", "sm"]);
	}
	get timeZone() {
		return this.userSettingsQuery.getValue().settings.timeZone;
	}
	get mode() {
		return this.userSettingsQuery.getValue()?.workspace.settings?.timesMode;
	}
	private _stateVisibleMode = this.recordService.group.value?.inputMode === "range";
	get stateVisibleMode() {
		return this._stateVisibleMode;
	}
	set stateVisibleMode(v: boolean) {
		if (this.userSettingsQuery.getValue().workspace?.settings?.timesMode === "range_optional") {
			const inputMode = v ? "range" : "duration";
			this.recordService.group.patchValue({
				inputMode,
			});
		}
		this._stateVisibleMode = v;
	}
	errorStateMatcher(ctrlName: string) {
		return new CustomFieldErrorStateMatcher(this.group.controls[ctrlName]);
	}
	getErrorObservable(controlName: string) {
		const aliasName =
			{
				name: "time.name",
			}[controlName] || controlName;
		return useFormErrorObservable(this)(
			controlName,
			() => this.group.controls[controlName],
			{
				required: (error, ctrl) => {
					if (controlName === "task") return { content: "errors.record.desc-required", args: {} };
					if (controlName === "project") return { content: "errors.record.project-req", args: {} };
					return {
						content: "errors.required",
						args: { field: aliasName },
					};
				},
				minlength: (error, ctrl) => ({
					content: "errors.minlength",
					args: { field: aliasName, length: error.requiredLength },
				}),
				blockEdit: (error, ctrl) => {
					return { content: "errors.times.comego.blockEdit", args: {} };
				},
				blockEditFuture: (error, ctrl) => {
					return { content: "errors.times.comego.blockEditFuture", args: {} };
				},
			},
			(key) => key,
			{
				initialValidate: true,
			}
		);
	}
	refreshGroup(data: typeof this.data) {
		const user = this.userSettingsQuery.getValue();
		const timeUser =
			data.meta.time?.user || user.workspace.users.find((x) => x.id === (data.meta.time as any).userId);
		const mode = this.mode;
		const project = this.projectsQuery.getEntity((data.meta.time as any).projectId);
		const start = data.start,
			end = data.end ?? roundToNearestMinutes(new Date(), { nearestTo: 1 });
		this.refTimeEntry.next(data.meta.time);
		this.patchValue(
			{
				name: data.title,
				start: formatString(start, "HH:mm"),
				end: formatString(end, "HH:mm"),
				tags: (data.meta.time.tags || []) as Tag[],
				date: new Date(data.start.getTime()),
				duration: parseDurationAsFormat((data.meta.time.timeDiff ?? 0) / 60),
				project: (data.meta.time.project ?? project) as Project,
				task: data.meta.time?.task as Task,
				billable: data.meta.time.billable,
				user: timeUser as any,
			},
			{ emitEvent: false }
		);
		this.group.setValidators([
			...(this.isRecording || mode === "duration" || !this.stateVisibleMode
				? []
				: [CustomValidators.timeDiffCheck("start", "end")]),
			CustomValidators.mustChange({
				...this.group.value,
				date: data.meta.oldStart ? new Date(data.meta.oldStart.getTime()) : new Date(data.start.getTime()),
				start: data.meta.oldStart ? formatString(data.meta.oldStart, "HH:mm") : formatString(start, "HH:mm"),
				...(this.isRecording
					? { end: undefined }
					: {
							end: data.meta.oldStart
								? formatString(data.meta.oldEnd, "HH:mm")
								: formatString(end, "HH:mm"),
						}),
			}),
		]);
		if (mode === "duration") {
			this.group.controls.duration.setValidators([
				Validators.required,
				CustomValidators.validDateFormat("HH:mm"),
				CustomValidators.minDuration(1000),
			]);
		} else if (mode !== "range" || this.isRecording) {
			this.group.controls.start.clearValidators(),
				this.group.controls.end.clearValidators(),
				this.group.controls.duration.clearValidators();
		} else {
			this.group.controls.start.setValidators([Validators.required, CustomValidators.validDateFormat("HH:mm")]),
				this.group.controls.duration.setValidators([
					Validators.required,
					CustomValidators.validDateFormat("HH:mm"),
				]),
				this.group.controls.end.setValidators([Validators.required, CustomValidators.validDateFormat("HH:mm")]);
		}

		if (this.requireSplit && data.meta.time) {
			const isDE = user.settings?.languageSetting?.split("-", 2)?.[0]?.toLowerCase() === "de";
			const segments = splitLongRangeToDay(new Date(data.meta.time.start), this.clock.now()).map(
				(d, i, arr): SplitTime => {
					const isLast = i === arr.length - 1;
					return {
						...(isLast
							? { start: startOfDay(d), end: setSeconds(roundToNearestMinutes(d), 0) }
							: { start: setSeconds(d, 0), end: setSeconds(endOfDay(d), 0) }),
						format: isDE ? "dd.MM" : "MM/dd",
					};
				}
			);
			this.recordingSplitSegment = segments[segments.length - 1] ?? null;
			this.splitTimes.next(segments);
			this.rebuildSplitForm(segments);
		} else {
			this.recordingSplitSegment = null;
			this.splitTimes.next([]);
			this.splitForm.clear();
		}
		this._stateVisibleMode = data.meta.time["inputMode"] === "range"; // set input mode according to time
		this.group.updateValueAndValidity();
		log.debug("data", data.meta.time);
	}
	readonly requireFields$ = this.userSettingsQuery.select().pipe(
		map((x) => x.workspace?.settings),
		map((x) => (x ? { name: !!x.requireName, project: !!x.requireProject, task: !!x.requireTask } : null))
	);
	get groupErrors() {
		const { errors } = this.group;
		if (!errors) return null;
		if (errors.range) return "errors.record.time-invalid-range";
		if (errors.same) return "errors.times.atleast_min";
		return null;
	}

	ngOnInit(): void {
		this.ref?.updateSize("820px", null);
		this.ref.addPanelClass(["mat-dialog-vanilla", "mat-dialog-relative"]);
		if (this.data.meta.fetchFromServer) {
			if (!this.data.meta?.time?.id) throw new Error("Time ID is required");
			this.isLoading = true;
			toPromise(
				this.timesService.adapter.get<Time>(environment.serverUrl + "/get/times/" + this.data.meta.time.id)
			)
				.then((time) => {
					this.data.meta.time = time;
					this.data.start = new Date(time.start);
					this.data.end = time.end ? new Date(time.end) : null;
					this.refreshGroup(this.data);
				})
				.finally(() => {
					this.isLoading = false;
				});
		} else {
			this.refreshGroup(this.data);
		}
		if (this.isRecording) {
			this.clock.now$.pipe(untilDestroyed(this)).subscribe((now) => {
				const endValue = formatString(now, "HH:mm");
				if (this.requireSplit && this.splitForm.length > 0) {
					if (!this.hasRecordingSplitRow) return;
					const last = this.splitForm.at(this.splitForm.length - 1);
					last.get("end")?.patchValue(endValue, { emitEvent: false });
				} else {
					this.group.controls.end.patchValue(endValue);
				}
			});
		}
		this.group.controls.start.valueChanges.subscribe(() => {
			this.group.controls.date.updateValueAndValidity();
		});
		this.group.controls.end.valueChanges.subscribe(() => {
			this.group.controls.date.updateValueAndValidity();
		});
		this.group.controls.user.valueChanges
			.pipe(
				map((u) => u?.id as string | undefined),
				distinctUntilChanged(),
				pairwise(),
				untilDestroyed(this)
			)
			.subscribe(([, userId]) => {
				this.invalidateProjectTaskForUser(userId);
			});
	}
	private invalidateProjectTaskForUser(userId?: string) {
		const me = this.userSettingsQuery.getValue();
		const booker = toBookingUserSettings(me, userId || me.id);
		const { project, task } = this.group.value;
		if (
			project &&
			!project.useAsDefault &&
			project.private &&
			!isProjectUser(booker.id, booker.workspace, project)
		) {
			this.resetProject();
			return;
		}
		if (task && project && !canBookOnTask(task, project, booker)) {
			this.patchValue({ task: null });
			this.appService.notifyInfo("time.task-not-available-for-user");
		}
	}
	openUserPicker() {
		if (!this.canChangeUser.value) return;
		return this.dialog
			.open(UserSinglePickerDialogComponent, {
				data: <UserSinglePickerDialogData>{
					selected: this.group.value?.user,
					filter: ({ selected, ...x }) => {
						const user = this.userSettingsQuery.getValue();
						const status = parseSubscriptionAsStatus(user.workspace, user);
						return ((x.has_license || status.isTrial) && !x.removed) || selected;
					},
				},
			})
			.afterClosed()
			.subscribe((user) => {
				if (user) {
					this.patchValue({
						user,
					});
					this.group.controls.date.updateValueAndValidity();
				}
			});
	}
	patchValue(data: Partial<typeof this.group.value> = {}, options?: Parameters<typeof this.group.patchValue>[1]) {
		const userId = this.data.meta?.time?.user?.id;
		const user = this.userSettingsQuery.getValue();
		if (data.user === null && userId)
			data.user =
				user.workspace.users.find((x) => x.id === userId) || user.workspace.users.find((x) => x.id === user.id);
		return this.group.patchValue(data, options);
	}
	onCalChange({ newStart, newEnd }: CalendarEventTimesChangedEvent) {
		const start = formatString(newStart, "HH:mm"),
			end = formatString(isSameDay(newStart, newEnd) ? newEnd : endOfDay(newStart.getTime()), "HH:mm");
		this.patchValue({
			start,
			end,
		});
	}
	resetProject() {
		this.patchValue({
			project: this.defaultProject,
			task: null,
		});

		this.group.controls.date.updateValueAndValidity();
	}
	openProjectPicker() {
		return this.dialog
			.open(ClientProjectPickerStepperDialogComponent, {
				data: <ClientProjectPickerStepperData>{
					skipProjectStepper: false,
					project: this.group.value.project,
					task: this.group.value.task,
					userId: this.group.value.user?.id ?? this.userSettingsQuery.getValue().id,
				},
			})
			.afterClosed()
			.subscribe((payload) => {
				if (!payload || !payload?.length)
					return ((ctrl) => (ctrl.markAsDirty(), ctrl.markAsTouched()))(this.group.controls.project);
				const [x, t]: [Project, Task] = payload || [];
				const newProject = x ?? this.defaultProject;
				this.patchValue({
					project: newProject,
					task: newProject?.useAsDefault ? null : t,
					billable: x.billable ? true : this.group.value.billable,
				});
				this.group.controls.date.updateValueAndValidity();
			});
	}
	close() {
		if (!this.ref.disableClose) this.ref.close();
	}
	async delete(confirmDeleteTemplate?: TemplateRef<unknown>) {
		const time = this.data?.meta?.time;
		if (!time?.id || !time.currentUserCanEdit) return;

		if (confirmDeleteTemplate) {
			const allowDelete = await firstValueFrom(this.dialog.open(confirmDeleteTemplate).afterClosed())
				.then((x) => x === true)
				.catch(() => false);
			if (!allowDelete) return;
		}

		this.deletionService.deleteWithUndo({ type: "timeEntry", entry: time });
		const deleteResult = {
			deleted: true as const,
			before: { start: new Date(time.start), end: new Date(time.end) },
		};
		this.mixedTimesSyncService.handleProjectTimeDialogClose(deleteResult, time);
		this.ref.close(deleteResult);
	}
	get closeDisabled() {
		return this.ref.disableClose;
	}
	openTagsPicker() {
		return this.dialog
			.open(TagPickerDialogComponent, {
				data: <TagDialogData>{
					data: {
						SelectedTags: this.group.value.tags,
						canToggle: true,
						type: TagType.Time,
						allowTypeModify: false,
					},
				},
			})
			.afterClosed()
			.pipe(filter((x) => !!x))
			.subscribe((x) => this.patchValue({ tags: x }));
	}
	openTaskPicker() {
		const { project, task } = this.group.value;
		if (!project || project.useAsDefault || project.completed) return;
		return this.dialog
			.open(ClientProjectPickerStepperDialogComponent, {
				data: <ClientProjectPickerStepperData>{
					skipProjectStepper: true,
					project: this.group.value.project,
					task: this.group.value.task,
					userId: this.group.value.user?.id ?? this.userSettingsQuery.getValue().id,
				},
			})
			.afterClosed()
			.subscribe((payload) => {
				if (!payload || !payload?.length)
					return ((ctrl) => (ctrl.markAsDirty(), ctrl.markAsTouched()))(this.group.controls.project);
				const [x, t]: [Project, Task] = payload || [];
				const newProject = x ?? this.defaultProject;
				this.patchValue({ task: newProject?.useAsDefault ? null : t });
			});
	}
	allowedDates() {
		const projectId = this.group.value.project.id;
		let project = this.projectsQuery.getAll().find((x) => x.id == projectId);
		if (project) {
			const start = getTimesLockedDate(this.userSettingsQuery.getValue(), project);
			const end = getTimesFutureLockDate(this.userSettingsQuery.getValue(), project);
			return [start, end];
		}
		return [null, null];
	}

	openCalPicker() {
		const dates = this.allowedDates();
		return this.dialog
			.open(TimeDatePickerComponent, {
				data: <TimeDatePickerConfig>{
					selectedDate: new Date(this.group.value.date),
					minDate: dates[0],
					maxDate: dates[1],
				},
			})
			.afterClosed()
			.pipe(filter((x) => x && isValid(x)))
			.subscribe((x) => {
				this.patchValue({
					date: x,
				});
			});
	}
	isProjectDefault(id: string) {
		return this.defaultProject.id === id;
	}
	get taskDisabled() {
		return (
			!this.group.value.project || this.group.value.project?.useAsDefault || this.group.value.project?.completed
		);
	}
	async updateSplit() {
		if ((this.group.invalid || this.splitForm.invalid) && !this.forceUpdate) return;
		const val = this.group.getRawValue();
		const mode = this.mode;
		const isRecording = this.isRecording;
		const time = this.data.meta.time ? { ...this.data.meta.time } : this.data.meta.time;
		const userSettings = this.userSettingsQuery.getValue();
		const user = this.data.meta.time?.user || userSettings.workspace.users.find((x) => x.id === userSettings.id);
		this.isLoading = true;
		const inputMode = this.stateVisibleMode ? "range" : "duration";
		let errorTimes: any[] = [],
			timesCreated: Time[] = [];
		const validSplitTimes = this.getSplitSegmentsFromForm();
		const queueTimes = validSplitTimes.map((split) => {
			const start: Date = split.start,
				end: Date = split.end;
			const relativeDateStart = startOfDay(start.getTime());
			const nextDuration =
				(({ hours, minutes }) => hours * 60 + minutes)(intervalToDuration({ start, end })) * 60;
			return {
				name: val.name,
				billable: !!val.billable,
				client: time.client,
				project: val.project || this.defaultProject,
				outlookCalenderReference: time.outlookCalenderReference,
				timeZone: time.timeZone,
				task: val.task,
				tags: val.tags,
				inputMode,
				...(mode === "range_optional"
					? this.stateVisibleMode
						? {
								start: start.toISOString(),
								end: end.toISOString(),
								timeDiff: 0,
								inputMode,
							}
						: {
								start: relativeDateStart,
								end: relativeDateStart,
								timeDiff: nextDuration,
								inputMode,
							}
					: mode === "duration"
						? {
								timeDiff: nextDuration,
								start: relativeDateStart,
								end: relativeDateStart,
							}
						: { start: start, end: end }),
				recordType: this.isRecording ? "timer" : "manual",
				...(val.user && val.user.id !== userSettings.id
					? { user: { id: val.user.id, name: val.user.name } }
					: {}),
			};
		});
		const confirmed = await this.mixedTimesSyncService.confirmProjectTimeCreate(queueTimes);
		if (!confirmed) {
			this.isLoading = false;
			return;
		}
		await firstValueFrom(this.myTimes.add(...(queueTimes as any[])))
			.then((x) => {
				this.myTimesQuery.__store__.remove(x.filter((y) => y && y.user?.id !== user.id).map((y) => y.id));
				timesCreated.push(...x);
			})
			.catch((err: any) => {
				this.recordService.handleError(err);
				errorTimes.push(err);
			});
		if (time && !time.end && timesCreated.length > 0) {
			await firstValueFrom(this.myTimes.delete(time)).catch((err) => {
				pushError(err);
			});
			(this.myTimesQuery.__store__ as MyTimesStore).remove((x) => !x.end);
		}
		this.isLoading = false;
		const closed = [timesCreated]?.flat?.().filter(Boolean);
		await this.recordService.afterProjectRecordingClosed(closed);
		return this.ref.close(closed);
	}
	async update(options?: { stopRecording?: boolean }) {
		const stopRecording = !!options?.stopRecording;
		if (this.isRecording && this.requireSplit && (stopRecording || !this.hasRecordingSplitRow))
			return await this.updateSplit();
		if (this.group.invalid && !this.forceUpdate) {
			if (stopRecording) this.group.markAllAsTouched();
			return;
		}
		const val = this.group.getRawValue();
		const mode = this.mode;
		const isRecording = this.isRecording;
		const stateVisibleMode = this.stateVisibleMode;
		const inputMode = stateVisibleMode ? "range" : "duration";
		this.isLoading = true;
		// the split view keeps its own start input, so never fall back to midnight while the timer runs on
		const keepSplitRecording = isRecording && this.requireSplit && !stopRecording;
		const start: Date = parseFromString(
			stateVisibleMode || keepSplitRecording ? val.start : "00:00",
			"HH:mm",
			val.date
		);
		const end: Date = parseFromString(stateVisibleMode ? val.end : "00:00", "HH:mm", val.date);
		const relativeDateStart = startOfDay(start.getTime());
		const time = this.data.meta.time;
		const originalSnapshot = structuredClone(time);
		const userSettings = this.userSettingsQuery.getValue();
		const user = this.data.meta.time?.user || userSettings.workspace.users.find((x) => x.id === userSettings.id);
		const nextDuration = isRecording
			? 0
			: inputMode === "duration"
				? parseDurationSeconds(val.duration)
				: (({ hours, minutes }) => hours * 60 + minutes)(intervalToDuration({ start, end })) * 60;
		const nextDurationEnd = addSeconds(start.getTime(), nextDuration);
		const stopRange =
			stopRecording && isRecording ? this.recordService.resolveRecordingStopRange(time.start) : null;
		return await firstValueFrom(
			this.myTimes.update(
				produce(time, (draft) => {
					return {
						...draft,
						name: val.name,
						billable: !!val.billable,
						project: val.project || this.defaultProject,
						task: val.task,
						tags: val.tags,
						inputMode,
						...(isRecording
							? stopRecording
								? {
										start: start.toISOString(),
										end: end.toISOString(),
										inputMode: stopRange.inputMode,
									}
								: {
										start: start.toISOString(),
										end: null,
									}
							: mode === "range_optional"
								? this.stateVisibleMode
									? {
											start: start.toISOString(),
											end: end.toISOString(),
											timeDiff: 0,
											inputMode,
										}
									: {
											start: relativeDateStart.toISOString(),
											end: relativeDateStart.toISOString(),
											timeDiff: nextDuration,
											inputMode,
										}
								: mode === "duration"
									? {
											timeDiff: nextDuration,
											start: start.toISOString(),
											end: nextDurationEnd.toISOString(),
											inputMode: "duration",
										}
									: { start: start.toISOString(), end: end.toISOString() }),
						recordType: this.isRecording ? "timer" : "manual",
						...(val.user && time.user?.id !== val.user?.id
							? { user: { id: val.user.id, name: val.user.name } }
							: {}),
					};
				})
			)
		)
			.finally(() => {
				this.isLoading = false;
			})
			.then(async (x) => {
				const updated = x?.find(Boolean);
				if (updated) {
					this.editUndoService.offerSingleUndo({ original: originalSnapshot, updated });
				} else {
					this.recordService.notifySuccess("success.updated");
				}
				this.myTimesQuery.__store__.remove(x.filter((y) => y && y.user?.id !== user.id).map((y) => y.id));
				if (isRecording && updated?.end) {
					await this.recordService.afterProjectRecordingClosed(x);
				} else if (!isRecording) {
					this.mixedTimesSyncService.handleProjectTimeDialogClose(x, originalSnapshot);
				}
				this.ref.close(x);
			})
			.catch((err: any) => {
				this.recordService.handleError(err);
				throw err;
			});
	}
	selectInput(ev: Event, timeInput: ElRefDirective) {
		const el: HTMLElement = timeInput.elementRef.nativeElement;
		if (!el) {
			return;
		}
		const input = el.querySelector("input");
		if (input) {
			ev.preventDefault();
			input.select();
		}
	}

	@ViewChild("timeContextMenuTrigger", { static: true })
	timeContextMenu: MatMenuTrigger;

	timeContextMenuPosition = { x: "0px", y: "0px" };

	onTimeContextMenuTrigger(event: MouseEvent, item: { time: Time; prop: string }) {
		event.stopPropagation(), event.preventDefault();
		this.timeContextMenuPosition.x = event.clientX + "px";
		this.timeContextMenuPosition.y = event.clientY + "px";
		this.timeContextMenu.menuData = { $implicit: item };
		this.timeContextMenu.menu.focusFirstItem("mouse");
		this.timeContextMenu.openMenu();
	}
	setToNow(prop: "start" | "end") {
		return this.group.controls[prop].patchValue(flow((date) => formatString(date, "HH:mm"))(new Date()));
	}
	setMinutesDiff(minutes: number, prop: "start" | "end") {
		switch (prop) {
			case "start":
				return this.group.controls[prop].patchValue(
					flow(parsefp(new Date(), "HH:mm"), subMinutes(minutes), clampDay(new Date()), (date) =>
						formatString(date, "HH:mm")
					)(this.group.value.end as string)
				);
			case "end":
				return this.group.controls[prop].patchValue(
					flow(
						parsefp(new Date(), "HH:mm"),
						(date) => addMinutes(date, minutes),
						clampDay(new Date()),
						(date) => formatString(date, "HH:mm")
					)(this.group.value.start as string)
				);
		}
	}
	setHourDiff(hours: number, prop: "start" | "end") {
		switch (prop) {
			case "start":
				return this.group.controls[prop].patchValue(
					flow(parsefp(new Date(), "HH:mm"), subHours(hours), clampDay(new Date()), (date) =>
						formatString(date, "HH:mm")
					)(this.group.value.end as string)
				);
			case "end":
				return this.group.controls[prop].patchValue(
					flow(parsefp(new Date(), "HH:mm"), addHours(hours), clampDay(new Date()), (date) =>
						formatString(date, "HH:mm")
					)(this.group.value.start as string)
				);
		}
	}
	setWorkDay() {
		return this.patchValue({
			start: "09:00",
			end: "17:00",
		});
	}

	focusCalendarNow() {
		const calContainer = this.el.nativeElement.querySelector("#cal-day-view");
		if (calContainer?.scrollTop !== undefined) {
			calContainer.scrollTop = this.data.start.getHours() * 30 * 4;
		}
	}
	stopRecording() {
		if (!this.canStopRecording) return;
		return this.update({ stopRecording: true });
	}

	calculateDuration(index: number) {
		const ctrl = this.getSplitGroup(index);
		if (!ctrl || ctrl.invalid) return null;
		const { start, end } = ctrl.getRawValue();
		const startMs = coerceTimeFormat(start)?.getTime(),
			endMs = coerceTimeFormat(end)?.getTime();
		if (!startMs || !endMs || endMs <= startMs) return null;
		return (endMs - startMs) / 1000;
	}
}
